import { Box, Button, Flex, MenuItem, Spacer, Text } from '@chakra-ui/react'
import { faUserMinus } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import React, { useMemo } from 'react'

import { getRandomNumberInRange } from '@/utils/getRandomNumberInRange'
import { pluralize } from '@/utils/helpers'

import { SeriesWithdrawalSource } from '@/queries/constants'
import RPC from '@/client/rpc'
import { EXPLAIN_REASONS, getSeriesWithdrawFormInputs } from '@/client/services/formInputs'
import { getSeriesUrlById } from '@/client/services/url'

import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

const ERROR_EMPTY_REASON = 'Please provide more details about your withdrawal reason'

export default function SeriesWithdrawButton({
  seriesId,
  memberId = null,
  memberName = null,
  isDisabled = false,
  isMenuItem = false,
  askForBootcampNPS = false,
  monthlySwitchLimit = null,
  redirectTo = null,
}: {
  seriesId: number
  memberId?: number | null
  memberName?: string | null
  isDisabled?: boolean
  isMenuItem?: boolean
  askForBootcampNPS?: boolean
  monthlySwitchLimit?: number | null
  redirectTo?: string | null
}): JSX.Element | null {
  const router = useRouter()
  const [showModal, setShowModal] = React.useState(false)
  const toast = useToaster()

  const isTutorWithdrawingMember = memberId != null

  const withdrawReasonFormInputs: FormInputType[] = useMemo(
    () => getSeriesWithdrawFormInputs({ isTutorWithdrawingMember, askForBootcampNPS }),
    [isTutorWithdrawingMember, askForBootcampNPS]
  )

  const handleWithdrawValidate = React.useCallback(
    (values: FormValues) =>
      EXPLAIN_REASONS.includes(`${values.reason}`) && `${values.reason_explain}`.length === 0
        ? {
            message: ERROR_EMPTY_REASON,
            errors: { name: ERROR_EMPTY_REASON },
          }
        : null,
    []
  )

  const handleWithdrawCallback = React.useCallback(
    async (values: FormValues) => {
      const reason_explain = `${values.reason_explain}`
      const bootcampNPS =
        values.bootcamp_nps == null ? null : Number.parseInt(`${values.bootcamp_nps}`, 10)
      await RPC.unregisterSeries({
        seriesId,
        userId: memberId,
        withdrawalSource: isTutorWithdrawingMember
          ? SeriesWithdrawalSource.Tutor
          : SeriesWithdrawalSource.Learner,
        withdrawalReason:
          reason_explain.length === 0 ? `${values.reason}` : `${values.reason}:${reason_explain}`,
        bootcampNPS,
      })
      toast({
        title: isTutorWithdrawingMember
          ? `${memberName} has successsfully been withdrawn from your series`
          : 'You have successfully withdrawn from this series!',
        status: 'success',
      })
      await router.push(
        // Add query param so Next.js doens't complain about hard-navigation to same url
        redirectTo ??
          `${getSeriesUrlById(
            seriesId,
            isTutorWithdrawingMember ? { page: 'members' } : undefined
          )}?withdrawn=${getRandomNumberInRange({ min: 0, max: 9999 })}`
      )
    },
    [router, seriesId, memberId, isTutorWithdrawingMember, memberName, toast, redirectTo]
  )

  return (
    <>
      {isMenuItem ? (
        <MenuItem
          borderRadius={0}
          cursor="pointer"
          value="withdraw"
          onClick={() => setShowModal(true)}
          color="fg.error"
          isDisabled={isDisabled}
          py="8px"
          px="16px"
        >
          <FontAwesomeIcon icon={faUserMinus} />
          <Text ml="12px" color="fg.error">
            Withdraw
          </Text>
        </MenuItem>
      ) : (
        <Button
          onClick={() => setShowModal(true)}
          variant="outline"
          color="fg.error"
          isDisabled={isDisabled}
          leftIcon={<FontAwesomeIcon icon={faUserMinus} />}
        >
          Withdraw
        </Button>
      )}
      <ModalV3
        size="lg"
        open={showModal}
        onClose={() => setShowModal(false)}
        title={isTutorWithdrawingMember ? `Withdraw ${memberName}?` : 'Withdraw from Series?'}
        body={() => (
          <>
            {isTutorWithdrawingMember ? (
              <Text align="left" mb="24px">
                You can withdraw this student according to your custom attendance policy and
                discretion. When appropriate, communicate with your student over chat before
                withdrawing them.
              </Text>
            ) : null}
            {monthlySwitchLimit != null && !isTutorWithdrawingMember ? (
              <Box bg="blue.subtle" p="16px" borderRadius="8px" mb="16px">
                <Text color="blue.fg" fontSize="sm">
                  You can only switch series in this program {pluralize(monthlySwitchLimit, 'time')}{' '}
                  per month. Withdrawing will count toward your monthly limit.
                </Text>
              </Box>
            ) : null}
            <Form
              name="SeriesWithdrawReason"
              onValidate={handleWithdrawValidate}
              FooterComponent={({ submitting }) => (
                <Flex mt="16px">
                  <Spacer />
                  <Button
                    variant="outline"
                    isDisabled={submitting}
                    onClick={() => {
                      setShowModal(false)
                    }}
                    mr="8px"
                  >
                    Close
                  </Button>
                  <Button variant="danger" isLoading={submitting} type="submit">
                    Withdraw
                  </Button>
                </Flex>
              )}
              blocks={withdrawReasonFormInputs}
              onSubmit={handleWithdrawCallback}
            />
          </>
        )}
      />
    </>
  )
}
